import "./index.css";
import Group from "../TagManage/Group";
import { Form, Input, message, Modal } from "antd";
import { request } from "../../../../request";
import React, { useMemo, useState } from "react";

import type { RuleObject } from "antd/es/form";
import type { TypeGuidelineSelectProps } from "..";

import type { TypeTagFilterItem } from "../TagManage";
import type { TypeGuideline, TypeTag } from "../../../hooks/useAllPrograms";

interface TypeEditSelectionProps
  extends Pick<TypeGuidelineSelectProps, "programs"> {
  /**
   * @param guidelineIds 当前选择的 guideline IDs
   * @description 如果有这个值，则开启弹窗进行编辑操作
   */
  guidelineIds?: Array<TypeGuideline["id"]>;
  /**
   * @name onClose 关闭弹窗
   */
  onClose(tagId?: TypeTag["id"]): void;
}

/**
 * @name EditSelection 保存当前选择的 guideline 组合
 * @description "Save this selection"
 */
const EditSelection: React.FC<TypeEditSelectionProps> = ({
  onClose,
  programs,
  guidelineIds,
}) => {
  const { TAGS, MAP_GUIDELINE_ID } = programs.data;

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<Pick<TypeTag, "name">>();

  async function onSubmit() {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const res = await request(`/api/v1/mortgagegpt/createSessionTag`, {
        method: "POST",
        body: { name: values.name, documents: guidelineIds },
      });
      onCancel(res?.data?.id);
      message.success("Selection created successfully!");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  function validator(_rule: RuleObject, value: string) {
    if (!value) return Promise.resolve();
    if (value.length > 20) return Promise.reject();
    if (TAGS.find((v) => v.name === value)) {
      return Promise.reject("Tag name already exists.");
    }
    return Promise.resolve();
  }

  function onCancel(id?: TypeTag["id"]) {
    form.resetFields();
    onClose(id);
  }

  const DATA_GROUP = useMemo(() => {
    if (!guidelineIds?.length) return [];
    const guidelines = {};
    for (const val of guidelineIds) {
      const item = MAP_GUIDELINE_ID[val];
      if (!item) continue;
      if (guidelines[item.lenderId]) {
        guidelines[item.lenderId].options.push(item);
      } else {
        guidelines[item.lenderId] = {
          options: [item],
          lenderId: item.lenderId,
          lenderName: item.lenderName,
          lenderDisplayName: item.lenderDisplayName,
        };
      }
    }
    return Object.values(guidelines) as TypeTagFilterItem["guidelines"];
  }, [guidelineIds, MAP_GUIDELINE_ID]);

  const name = Form.useWatch("name", form);

  return (
    <Modal
      centered
      width={566}
      okText="Create"
      onOk={onSubmit}
      title="Save selection"
      onCancel={() => onCancel()}
      cancelButtonProps={{ type: "text" }}
      open={Boolean(guidelineIds?.length)}
      okButtonProps={{ disabled: !name, loading }}
      className="guideline-modal edit-guideline-selection"
    >
      <p>Save your frequently used guidelines for quick reuse.</p>
      <div className="edit-selection-title">
        <span className="edit-selection-name">Name</span>
        <span
          className="edit-selection-desc"
          style={{ color: name?.length > 20 ? "#C21818" : "" }}
        >
          {name?.length || 0}/20 characters
        </span>
      </div>
      <Form form={form}>
        <Form.Item name="name" rules={[{ validator }]}>
          <Input
            allowClear
            placeholder="Enter name"
            className="edit-selection-input"
          />
        </Form.Item>
      </Form>
      <p className="edit-selection-selected">
        {guidelineIds?.length} guidelines
      </p>
      {DATA_GROUP.map((v) => (
        <Group.Item
          noEditing
          key={v.lenderId}
          options={v.options}
          name={v.lenderDisplayName}
        />
      ))}
    </Modal>
  );
};

export default EditSelection;
